/* eslint-disable react/jsx-key */
import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, List, Modal, Skeleton, Switch, Tooltip, message } from "antd";
import { capitalize, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { REMOVE_LEAD_FROM_CAMPAIGN } from "../../../../graphql/mutation";
import { GET_CAMPAIGN_BY_LEAD } from "../../../../graphql/query";
import useLang from "../../../../hooks/useLang";
import { closeDialog, openDialog } from "../../../../library/helpers";
import { setLoaderStatus } from "../../../../redux/slices/loaderSlice";
import AntdIconV4 from "../../../../utils/antdIcon";
import LeadCampaignViewModal from "./leadCampaignViewModal";
import StopCampaignConfirmation from "./StopCampaignConfirmation";
import { ConfirmPopup } from "../../../commonComponents/ConfirmPopup";

const ListContainer = styled.div`
  & .ant-list-item-meta {
    text-align: left;
    margin-bottom:0
  }
  & .ant-list-item-meta-title{
    font-size:16px;
    margin-bottom:0
  }
  & .ant-list-item-meta-description > p{
    margin:bottom:0.4rem;
  }
  & .ant-list-item-meta-content {
    margin:bottom:0;
    padding-top:0 !important;
  }
`;

const CampignListView = ({
  showLinks = false,
  onMessageFilter = null,
  refetchLeads = () => {},
  onClose = () => {},
  handleLeadDetailProfile = () => {},
  setliveCampaign,
}) => {
  const [lang] = useLang();
  const { selectedLead } = useSelector(state => state.lead);
  const [stopCampaignConfirmation, setstopCampaignConfirmation] = useState({
    visible: false,
    item: {},
  });
  const dispatch = useDispatch();

  const [variables, setVariables] = useState({
    page: 1,
    pageSize: 25,
    leadId: +selectedLead?.id,
  });
  const [openCampaignPopup, setOpenCampaignPopup] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [getCampaign, { data, loading, refetch: refetchCampaigns }] =
    useLazyQuery(GET_CAMPAIGN_BY_LEAD);

  const [removeLeadFromCampaign, { loading: removeLeadLoading }] =
    useMutation(REMOVE_LEAD_FROM_CAMPAIGN);

  useEffect(() => {
    if (selectedLead?.id) {
      setVariables({ ...variables, leadId: +selectedLead?.id });
      getCampaign({ variables: { ...variables, leadId: +selectedLead?.id } });
    }
  }, [selectedLead?.id]);

  const count = data?.getCampaignByLead?.count || 0;
  setliveCampaign(
    !isEmpty(
      data?.getCampaignByLead?.data.find(campaign => campaign.activeInd.toLowerCase() === "active")
    )
  );
  const onTitleClick = item => {
    setOpenCampaignPopup(true);
    setSelectedCampaign(item);
  };

  const onRemoveLead = item => {
    setstopCampaignConfirmation({ visible: true, item });
  };
  const campaignColorCode = status => {
    switch (status.toLowerCase()) {
      case "active":
        return "text-success-500";
      case "inactive":
        return "text-red-500";
      case "draft":
        return "text-grey-500";
      default:
        break;
    }
  };
  return (
    <ListContainer>
      <Skeleton loading={loading || removeLeadLoading} active avatar>
        <List
          itemLayout="vertical"
          key="lead_grid_view"
          bordered
          dataSource={data?.getCampaignByLead?.data || []}
          pagination={
            count < 5
              ? null
              : {
                  defaultCurrent: variables.page,
                  current: variables.page,
                  defaultPageSize: variables.pageSize,
                  pageSize: variables.pageSize,
                  total: count || 0,
                  showTotal: total =>
                    `${lang.total}: ${total} ${
                      total === 1 ? lang.campaign.toLowerCase() : lang.campaigns.toLowerCase()
                    }`,
                  pageSizeOptions: ["5", "25", "50"],
                  showSizeChanger: true,
                  onChange: (page, pageSize) => {
                    const newVariables = {
                      ...variables,
                      page: page,
                      pageSize: pageSize,
                    };
                    setVariables(newVariables);
                  },
                  onShowSizeChange: (current, size) => {
                    const newVariables = {
                      ...variables,
                      page: current,
                      pageSize: size,
                    };
                    setVariables(newVariables);
                  },
                }
          }
          renderItem={item => {
            const links = [];
            const HorizontalLine = () => <div className="h-3 w-[1px] bg-grey-500 " />;
            if (showLinks) {
              links.push(
                <div className="cursor-pointer" onClick={() => onTitleClick(item)}>
                  <Tooltip placement="topLeft" title={lang.viewCampaignDetails}>
                    {lang.details}
                  </Tooltip>
                </div>
              );
              links.push(
                <>
                  <HorizontalLine />
                  <div className="cursor-pointer" onClick={() => onMessageFilter(item.id)}>
                    <Tooltip placement="topLeft" title={lang.filterMsgByCampaign}>
                      {lang.message}
                    </Tooltip>
                  </div>
                </>
              );
              links.push(
                <>
                  <HorizontalLine />
                  <div
                    className="cursor-pointer"
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveLead(item);
                    }}
                  >
                    <Tooltip placement="topLeft" title={lang.removeLeadFromCampaign}>
                      {lang.remove}
                    </Tooltip>
                  </div>
                </>
              );
            }
            return (
              <div className="rounded-[4px]">
                <div className="p-2 flex-col-centered gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-grey-700 font-[600]">{item?.name || ""}</span>
                    <Switch disabled={item.activeInd.toLowerCase() !== "active"} />
                  </div>
                  <span className={`text-sm font-[400] ${campaignColorCode(item.activeInd)}`}>
                    {capitalize(item.activeInd)}
                  </span>
                </div>
                {/* Footer */}
                <div className="flex-row-centered px-2 py-1 bg-grey-100 rounded-b-[4px]  items-center gap-3">
                  {links.map(item => item)}
                </div>
              </div>
            );
          }}
        />
      </Skeleton>
      {openCampaignPopup && (
        <LeadCampaignViewModal
          campaign={{ item: selectedCampaign }}
          visible={openCampaignPopup}
          onCancel={() => setOpenCampaignPopup(false)}
          handleLeadDetailProfile={handleLeadDetailProfile}
        />
      )}
      <ConfirmPopup
        onOk={async () => {
          dispatch(setLoaderStatus({ open: true }));
          try {
            const resp = await removeLeadFromCampaign({
              variables: {
                campaignId: parseInt(stopCampaignConfirmation.item.id),
                leadIds: [+selectedLead?.id],
              },
            });
            if (resp?.data?.removeLeadsFromCampaign?.statusCode === 200) {
              refetchCampaigns();
              refetchLeads();
              setstopCampaignConfirmation({ visible: false, item: {} });
            } else {
              message.error(lang.somethingWentWrong);
            }
          } catch (err) {
            console.log("Error in onRemoveLead", err);
            message.error(lang.somethingWentWrong);
          } finally {
            dispatch(setLoaderStatus({ open: false }));
          }
        }}
        confirmData={stopCampaignConfirmation}
        setConfirmData={setstopCampaignConfirmation}
        okLoading={removeLeadLoading}
        okText="Yes, Turn off"
        content={<StopCampaignConfirmation />}
      />
    </ListContainer>
  );
};

export default CampignListView;
